import { getPossibleMoves } from '../utils/movement';
import { getPossibleWalls } from '../utils/walls';
import { BOARD_SIZE } from '../constants';

// Helper function to calculate Manhattan distance between two points
const manhattanDistance = (p1, p2) => Math.abs(p1.row - p2.row) + Math.abs(p1.col - p2.col);

// Helper function to count walls around a cell
const countWallsAround = (walls, row, col) => {
  let count = 0;
  // Check horizontal walls
  if (row > 0 && walls.horizontal[row][col]) count++;
  if (row < BOARD_SIZE - 1 && walls.horizontal[row + 1][col]) count++;
  // Check vertical walls
  if (col > 0 && walls.vertical[row][col]) count++;
  if (col < BOARD_SIZE - 1 && walls.vertical[row][col + 1]) count++;
  return count;
};

// Helper function to check if a cell is on the border
const isBorderCell = (row, col) =>
  row === 0 || row === BOARD_SIZE - 1 || col === 0 || col === BOARD_SIZE - 1;

// Helper function to find enemy stones
const findEnemyStones = (board, currentPlayer) => {
  const enemyStones = [];
  const enemyColor = currentPlayer === 'black' ? 'white' : 'black';

  for (let i = 0; i < board.length; i++) {
    for (let j = 0; j < board.length; j++) {
      if (board[i][j] === enemyColor) {
        enemyStones.push({ row: i, col: j });
      }
    }
  }
  return enemyStones;
};

// Helper function to find player stones
const findPlayerStones = (board, currentPlayer) => {
  const playerStones = [];
  for (let i = 0; i < board.length; i++) {
    for (let j = 0; j < board.length; j++) {
      if (board[i][j] === currentPlayer) {
        playerStones.push({ row: i, col: j });
      }
    }
  }
  return playerStones;
};

// Helper function to find shortest path using BFS
const findShortestPath = (start, target, board, walls) => {
  const queue = [{ pos: start, distance: 0 }];
  const visited = new Set();
  visited.add(`${start.row},${start.col}`);

  while (queue.length > 0) {
    const { pos, distance } = queue.shift();

    if (pos.row === target.row && pos.col === target.col) {
      return distance;
    }

    // Check all four directions
    const directions = [
      { row: pos.row - 1, col: pos.col }, // up
      { row: pos.row + 1, col: pos.col }, // down
      { row: pos.row, col: pos.col - 1 }, // left
      { row: pos.row, col: pos.col + 1 }  // right
    ];

    for (const nextPos of directions) {
      // Skip if out of bounds
      if (nextPos.row < 0 || nextPos.row >= BOARD_SIZE ||
        nextPos.col < 0 || nextPos.col >= BOARD_SIZE) {
        continue;
      }

      // Skip if already visited
      const key = `${nextPos.row},${nextPos.col}`;
      if (visited.has(key)) {
        continue;
      }

      // Check if there's a wall blocking the path
      const hasWall = (
        // Check horizontal walls
        (nextPos.row > pos.row && walls.horizontal[nextPos.row][pos.col]) ||
        (nextPos.row < pos.row && walls.horizontal[pos.row][pos.col]) ||
        // Check vertical walls
        (nextPos.col > pos.col && walls.vertical[pos.row][nextPos.col]) ||
        (nextPos.col < pos.col && walls.vertical[pos.row][pos.col])
      );

      if (!hasWall) {
        queue.push({ pos: nextPos, distance: distance + 1 });
        visited.add(key);
      }
    }
  }

  return Infinity; // No path found
};

// Helper function to find valid paths to enemy stones
const findValidPathsToEnemy = (start, board, walls, currentPlayer) => {
  const enemyStones = findEnemyStones(board, currentPlayer);
  const queue = [{ pos: start, distance: 0, path: [start] }];
  const visited = new Set();
  visited.add(`${start.row},${start.col}`);
  const validPaths = [];

  while (queue.length > 0) {
    const { pos, distance, path } = queue.shift();

    // Check if we're adjacent to any enemy stone
    for (const enemyStone of enemyStones) {
      const isAdjacent = (
        (Math.abs(pos.row - enemyStone.row) === 1 && pos.col === enemyStone.col) ||
        (Math.abs(pos.col - enemyStone.col) === 1 && pos.row === enemyStone.row)
      );

      if (isAdjacent) {
        // Check if there's no wall between us and the enemy
        const hasWall = (
          (pos.row > enemyStone.row && walls.horizontal[pos.row][pos.col]) ||
          (pos.row < enemyStone.row && walls.horizontal[enemyStone.row][pos.col]) ||
          (pos.col > enemyStone.col && walls.vertical[pos.row][pos.col]) ||
          (pos.col < enemyStone.col && walls.vertical[pos.row][enemyStone.col])
        );

        if (!hasWall) {
          validPaths.push({ path, distance, target: enemyStone });
        }
      }
    }

    // Check all four directions
    const directions = [
      { row: pos.row - 1, col: pos.col }, // up
      { row: pos.row + 1, col: pos.col }, // down
      { row: pos.row, col: pos.col - 1 }, // left
      { row: pos.row, col: pos.col + 1 }  // right
    ];

    for (const nextPos of directions) {
      // Skip if out of bounds
      if (nextPos.row < 0 || nextPos.row >= BOARD_SIZE ||
        nextPos.col < 0 || nextPos.col >= BOARD_SIZE) {
        continue;
      }

      // Skip if already visited
      const key = `${nextPos.row},${nextPos.col}`;
      if (visited.has(key)) {
        continue;
      }

      // Skip if there's a stone in the way
      if (board[nextPos.row][nextPos.col]) {
        continue;
      }

      // Check if there's a wall blocking the path
      const hasWall = (
        // Check horizontal walls
        (nextPos.row > pos.row && walls.horizontal[nextPos.row][pos.col]) ||
        (nextPos.row < pos.row && walls.horizontal[pos.row][pos.col]) ||
        // Check vertical walls
        (nextPos.col > pos.col && walls.vertical[pos.row][nextPos.col]) ||
        (nextPos.col < pos.col && walls.vertical[pos.row][pos.col])
      );

      if (!hasWall) {
        queue.push({
          pos: nextPos,
          distance: distance + 1,
          path: [...path, nextPos]
        });
        visited.add(key);
      }
    }
  }

  return validPaths;
};

// Helper function to evaluate a move
const evaluateMove = (move, board, walls, currentPlayer) => {
  let score = 0;

  // Find valid paths to enemy stones from this position
  const validPaths = findValidPathsToEnemy(move, board, walls, currentPlayer);

  if (validPaths.length > 0) {
    // Sort paths by distance
    validPaths.sort((a, b) => a.distance - b.distance);
    const bestPath = validPaths[0];

    // If we're already adjacent to an enemy stone
    if (bestPath.distance === 0) {
      score += 1000; // Maximum score for being adjacent to enemy
    } else {
      // Score based on how close we are to an enemy stone
      score += (1000 - bestPath.distance * 100);
    }
  } else {
    // No valid paths to enemy stones
    score -= 1000;
  }

  // Penalize moves that put us in a corner or against walls
  const wallsAround = countWallsAround(walls, move.row, move.col);
  score -= wallsAround * 50;

  return score;
};

// Helper function to evaluate a wall placement
const evaluateWallPlacement = (wall, board, walls, currentPlayer) => {
  let score = 0;

  // Check if this wall would be adjacent to any enemy stone
  for (const enemyStone of findEnemyStones(board, currentPlayer)) {
    const isAdjacent = (
      (wall.type === 'horizontal' &&
        ((wall.row === enemyStone.row && wall.col === enemyStone.col) ||
          (wall.row === enemyStone.row + 1 && wall.col === enemyStone.col))) ||
      (wall.type === 'vertical' &&
        ((wall.col === enemyStone.col && wall.row === enemyStone.row) ||
          (wall.col === enemyStone.col + 1 && wall.row === enemyStone.row)))
    );

    if (isAdjacent) {
      // Check if there's a player stone adjacent to the enemy stone
      const hasAdjacentPlayerStone = findPlayerStones(board, currentPlayer).some(stone =>
        (Math.abs(stone.row - enemyStone.row) === 1 && stone.col === enemyStone.col) ||
        (Math.abs(stone.col - enemyStone.col) === 1 && stone.row === enemyStone.row)
      );

      if (hasAdjacentPlayerStone) {
        // Count existing walls around this enemy stone
        const wallsAround = countWallsAround(walls, enemyStone.row, enemyStone.col);

        // Check if this wall would complete an encirclement
        if (wallsAround === 3) {
          score += 2000; // Massive bonus for completing encirclement
        } else {
          score += (wallsAround + 1) * 100; // Bonus for contributing to encirclement
        }
      }
    }
  }

  // Penalize walls that would trap our own stones
  for (const stone of findPlayerStones(board, currentPlayer)) {
    const wallsAround = countWallsAround(walls, stone.row, stone.col);
    if (wallsAround >= 3) {
      // Check if the stone would be trapped
      const hasEscapePath = findPlayerStones(board, currentPlayer).some(otherStone =>
        otherStone !== stone &&
        findShortestPath(stone, otherStone, board, walls) !== Infinity
      );

      if (!hasEscapePath) {
        score -= 1000; // Huge penalty for trapping our own stones
      }
    }
  }

  // Penalize walls that don't contribute to encirclement
  if (score === 0) {
    score -= 500; // Increased penalty for non-strategic wall placement
  }

  return score;
};

// Helper function to evaluate a stone placement position
const evaluatePlacementPosition = (row, col, board, currentPlayer, walls) => {
  let score = 0;
  const enemyStones = findEnemyStones(board, currentPlayer);

  // Penalize border positions heavily
  if (isBorderCell(row, col)) {
    score -= 500; // Increased penalty for border positions
  }

  // Find closest enemy stone
  let minPathDistance = Infinity;
  for (const enemyStone of enemyStones) {
    const pathDistance = findShortestPath({ row, col }, enemyStone, board, walls);
    minPathDistance = Math.min(minPathDistance, pathDistance);
  }

  if (minPathDistance !== Infinity) {
    // Prefer positions that are close to enemy stones but not too close
    if (minPathDistance <= 3) {
      score += 200; // Bonus for being close to enemy
    } else if (minPathDistance <= 5) {
      score += 100; // Moderate bonus for being moderately close
    }
  }

  // Check walls around the position
  const wallsAround = countWallsAround(walls, row, col);
  if (wallsAround >= 3) {
    score -= 300; // Heavy penalty for placing in a nearly surrounded position
  }

  // Penalize positions too close to other stones
  const minDistance = 3; // Reduced minimum distance to allow closer placement
  for (let i = 0; i < board.length; i++) {
    for (let j = 0; j < board.length; j++) {
      if (board[i][j]) {
        const distance = manhattanDistance({ row, col }, { row: i, col: j });
        if (distance < minDistance) {
          score -= (minDistance - distance) * 50;
        }
      }
    }
  }

  return score;
};

export const makeNormalMove = ({
  turnAction,
  board,
  currentPlayer,
  walls,
  separatedCellsGroups,
  selectedStone,
  handleCellClick,
  handleWallClick
}) => {
  if (turnAction === 'place') {
    // Find empty cells and evaluate each one
    const emptyCells = [];
    for (let i = 0; i < board.length; i++) {
      for (let j = 0; j < board.length; j++) {
        if (!board[i][j]) {
          const score = evaluatePlacementPosition(i, j, board, currentPlayer, walls);
          emptyCells.push({ row: i, col: j, score });
        }
      }
    }

    if (emptyCells.length > 0) {
      // Sort by score in descending order
      emptyCells.sort((a, b) => b.score - a.score);
      // Choose the best position
      handleCellClick(emptyCells[0].row, emptyCells[0].col, true);
    }
  } else if (turnAction === 'move' && selectedStone) {
    const validMoves = getPossibleMoves(board, walls, selectedStone.row, selectedStone.col);

    if (validMoves.length > 0) {
      // Evaluate each possible move
      const evaluatedMoves = validMoves.map(move => ({
        ...move,
        score: evaluateMove(move, board, walls, currentPlayer)
      }));

      // Sort by score in descending order
      evaluatedMoves.sort((a, b) => b.score - a.score);
      // Choose the best move
      handleCellClick(evaluatedMoves[0].row, evaluatedMoves[0].col, true);
    }
  } else if (turnAction === 'move') {
    // Get all stones of current player
    const playerStones = findPlayerStones(board, currentPlayer);
    const settledStones = [];

    // Try stones until we find one that can move
    while (playerStones.length > 0) {
      // Evaluate each stone's potential moves
      const stoneEvaluations = playerStones.map(stone => {
        const group = separatedCellsGroups?.find(group =>
          group.cells.some(cell => cell.row === stone.row && cell.col === stone.col)
        );

        const validMoves = getPossibleMoves(board, walls, stone.row, stone.col);
        const bestMoveScore = validMoves.length > 0
          ? Math.max(...validMoves.map(move => evaluateMove(move, board, walls, currentPlayer)))
          : -Infinity;

        return {
          stone,
          isSettled: group?.settled || false,
          bestMoveScore
        };
      });

      // Sort by best move score in descending order
      stoneEvaluations.sort((a, b) => b.bestMoveScore - a.bestMoveScore);

      const bestStone = stoneEvaluations[0];

      if (bestStone.isSettled) {
        // Remove settled stone from consideration
        settledStones.push(bestStone.stone);
        playerStones.splice(playerStones.indexOf(bestStone.stone), 1);
        continue;
      }

      if (bestStone.bestMoveScore === -Infinity) {
        // Remove stone without valid moves from consideration
        playerStones.splice(playerStones.indexOf(bestStone.stone), 1);
        continue;
      }

      // Found a stone with valid moves
      handleCellClick(bestStone.stone.row, bestStone.stone.col, true);
      return;
    }

    // If we get here, we need to move a settled stone
    for (const stone of settledStones) {
      const validMoves = getPossibleMoves(board, walls, stone.row, stone.col);
      if (validMoves.length === 0) continue;

      // Evaluate moves for settled stone
      const evaluatedMoves = validMoves.map(move => ({
        ...move,
        score: evaluateMove(move, board, walls, currentPlayer)
      }));

      evaluatedMoves.sort((a, b) => b.score - a.score);
      handleCellClick(stone.row, stone.col, true);
      return;
    }
  } else if (turnAction === 'wall') {
    const possibleWalls = getPossibleWalls(walls, selectedStone);

    if (possibleWalls.length > 0) {
      // Evaluate each possible wall placement
      const evaluatedWalls = possibleWalls.map(wall => ({
        ...wall,
        score: evaluateWallPlacement(wall, board, walls, currentPlayer)
      }));

      // Sort by score in descending order
      evaluatedWalls.sort((a, b) => b.score - a.score);
      // Choose the best wall placement
      handleWallClick(evaluatedWalls[0].type, evaluatedWalls[0].row, evaluatedWalls[0].col, true);
    }
  }
};
